import { useCallback, useEffect, useState } from 'react';

import { listBrands } from '../api/brands.api';
import type { Brand } from '../api/brands.api';
import { useAuth } from '../context/AuthContext';

export function useBrandsData() {
  const { user } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by refresh() to re-run the fetch without duplicating logic.
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!user) {
      setBrands([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const data = await listBrands();
        if (cancelled) return;
        setBrands(data);
      } catch (err: unknown) {
        if (cancelled) return;
        setError((err as { message?: string }).message ?? 'Failed to load brands');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  return { brands, loading, error, refresh };
}
